import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { liveInterviewService, ParticipantRole } from '@/services/api';

type CallStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'ended'
  | 'error';
export type UserType = 'candidate' | 'interviewer';

interface SessionDetails {
  uId: string;
  candidateUId: string;
  interviewerUId: string;
  slotBookingUId: string | null;
  sessionStatus: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime: string | null;
  actualEndTime: string | null;
  acsThreadId: string | null;
  candidateAcsUserId: string;
  interviewerAcsUserId: string | null;
  recordingUrl: string | null;
  durationInMinutes: number | null;
  candidateJoinedAt: string | null;
  interviewerJoinedAt: string | null;
}

interface VideoCallState {
  // ACS credentials
  acsUserId: string | null;
  accessToken: string | null;
  tokenExpiresOn: string | null;

  // Session info
  sessionDetails: SessionDetails | null;
  userType: UserType | null;
  userUId: string | null;

  // Access validation
  hasAccess: boolean | null;
  isValidatingAccess: boolean;

  // Call state
  callStatus: CallStatus;
  callId: string | null;

  // Media state
  isVideoOn: boolean;
  isAudioOn: boolean;
  isScreenSharing: boolean;

  // Remote participant
  remoteParticipantConnected: boolean;
  remoteVideoOn: boolean;
  remoteAudioOn: boolean;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Call duration
  callStartTime: string | null;
  callEndTime: string | null;
}

const initialState: VideoCallState = {
  acsUserId: null,
  accessToken: null,
  tokenExpiresOn: null,
  sessionDetails: null,
  userType: null,
  userUId: null,
  hasAccess: null,
  isValidatingAccess: false,
  callStatus: 'idle',
  callId: null,
  isVideoOn: true,
  isAudioOn: true,
  isScreenSharing: false,
  remoteParticipantConnected: false,
  remoteVideoOn: false,
  remoteAudioOn: false,
  isLoading: false,
  error: null,
  callStartTime: null,
  callEndTime: null,
};

// Helper to convert userType to ParticipantRole
const toParticipantRole = (userType: UserType): ParticipantRole => {
  return userType === 'interviewer' ? 'Interviewer' : 'Candidate';
};

// Async thunk: Get session details
const getSessionDetails = createAsyncThunk(
  'videoCall/getSessionDetails',
  async (sessionUId: string, { rejectWithValue }) => {
    try {
      const response = await liveInterviewService.getSessionByUId({
        uId: sessionUId,
      });
      if (response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to get session details'
        );
      }
      return response.data;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to get session details';
      return rejectWithValue(message);
    }
  }
);

// Async thunk: Validate session access
const validateSessionAccess = createAsyncThunk(
  'videoCall/validateSessionAccess',
  async (
    params: { sessionUId: string; userType: UserType; userUId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await liveInterviewService.validateSessionAccess({
        sessionUId: params.sessionUId,
        participantRole: toParticipantRole(params.userType),
        userUId: params.userUId,
      });
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Access validation failed');
      }
      return {
        hasAccess: response.data.hasAccess,
        userType: params.userType,
        userUId: params.userUId,
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to validate access';
      return rejectWithValue(message);
    }
  }
);

// Async thunk: Generate ACS token
export const fetchAcsToken = createAsyncThunk(
  'videoCall/fetchAcsToken',
  async (
    params: { sessionUId: string; userType: UserType; userUId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await liveInterviewService.generateToken({
        sessionUId: params.sessionUId,
        participantRole: toParticipantRole(params.userType),
        userUId: params.userUId,
      });
      sessionStorage.setItem('userId', params.userUId);
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to generate token');
      }
      return {
        acsUserId: response.data.acsUserId,
        accessToken: response.data.accessToken,
        expiresOn: response.data.expiresOn,
        sessionDetails: response.data.sessionDetails,
        userType: params.userType,
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch ACS token';
      return rejectWithValue(message);
    }
  }
);

// Async thunk: Join session (notify server that user joined)
export const joinSession = createAsyncThunk(
  'videoCall/joinSession',
  async (
    params: { sessionUId: string; userType: UserType },
    { rejectWithValue }
  ) => {
    try {
      const response = await liveInterviewService.joinSession({
        sessionUId: params.sessionUId,
        participantRole: toParticipantRole(params.userType),
      });
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to join session');
      }
      return response.data;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to join session';
      return rejectWithValue(message);
    }
  }
);

// Async thunk: Start session
const startSession = createAsyncThunk(
  'videoCall/startSession',
  async (sessionUId: string, { rejectWithValue }) => {
    try {
      const response = await liveInterviewService.startSession({ sessionUId });
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to start session');
      }
      return response.data;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to start session';
      return rejectWithValue(message);
    }
  }
);

// Async thunk: End session
export const endSession = createAsyncThunk(
  'videoCall/endSession',
  async (sessionUId: string, { rejectWithValue }) => {
    try {
      const response = await liveInterviewService.endSession({ sessionUId });
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to end session');
      }
      return response.data;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to end session';
      return rejectWithValue(message);
    }
  }
);

// Slice
const videoCallSlice = createSlice({
  name: 'videoCall',
  initialState,
  reducers: {
    setCallStatus: (state, action: PayloadAction<CallStatus>) => {
      state.callStatus = action.payload;
      if (action.payload === 'connected' && !state.callStartTime) {
        state.callStartTime = new Date().toISOString();
      }
      if (action.payload === 'ended' || action.payload === 'disconnected') {
        state.callEndTime = new Date().toISOString();
      }
    },
    setCallId: (state, action: PayloadAction<string | null>) => {
      state.callId = action.payload;
    },

    setVideoOn: (state, action: PayloadAction<boolean>) => {
      state.isVideoOn = action.payload;
    },
    setAudioOn: (state, action: PayloadAction<boolean>) => {
      state.isAudioOn = action.payload;
    },

    setScreenSharing: (state, action: PayloadAction<boolean>) => {
      state.isScreenSharing = action.payload;
    },
    setRemoteParticipant: (
      state,
      action: PayloadAction<{
        connected: boolean;
        videoOn?: boolean;
        audioOn?: boolean;
      }>
    ) => {
      state.remoteParticipantConnected = action.payload.connected;
      if (action.payload.videoOn !== undefined) {
        state.remoteVideoOn = action.payload.videoOn;
      }
      if (action.payload.audioOn !== undefined) {
        state.remoteAudioOn = action.payload.audioOn;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    resetVideoCall: () => initialState,
  },
  extraReducers: builder => {
    // Get Session Details
    builder
      .addCase(getSessionDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSessionDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionDetails = action.payload;
      })
      .addCase(getSessionDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Validate Session Access
    builder
      .addCase(validateSessionAccess.pending, state => {
        state.isValidatingAccess = true;
        state.error = null;
      })
      .addCase(validateSessionAccess.fulfilled, (state, action) => {
        state.isValidatingAccess = false;
        state.hasAccess = action.payload.hasAccess;
        state.userType = action.payload.userType;
        state.userUId = action.payload.userUId;
      })
      .addCase(validateSessionAccess.rejected, (state, action) => {
        state.isValidatingAccess = false;
        state.hasAccess = false;
        state.error = action.payload as string;
      });

    // Fetch ACS Token
    builder
      .addCase(fetchAcsToken.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAcsToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.acsUserId = action.payload.acsUserId;
        state.accessToken = action.payload.accessToken;
        state.tokenExpiresOn = action.payload.expiresOn;
        state.sessionDetails = action.payload.sessionDetails;
        state.userType = action.payload.userType;
      })
      .addCase(fetchAcsToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Join Session
    builder
      .addCase(joinSession.pending, state => {
        state.isLoading = true;
      })
      .addCase(joinSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionDetails = action.payload;
      })
      .addCase(joinSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Start Session
    builder
      .addCase(startSession.pending, state => {
        state.isLoading = true;
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionDetails = action.payload;
      })
      .addCase(startSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // End Session
    builder
      .addCase(endSession.pending, state => {
        state.isLoading = true;
      })
      .addCase(endSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionDetails = action.payload;
        state.callStatus = 'ended';
        state.callEndTime = new Date().toISOString();
      })
      .addCase(endSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCallStatus,
  setCallId,
  setVideoOn,
  setAudioOn,
  setScreenSharing,
  setRemoteParticipant,
  setError,
  resetVideoCall,
} = videoCallSlice.actions;

export default videoCallSlice.reducer;
